import { Service } from "dioc"
import * as E from "fp-ts/Either"
import { computed, readonly, ref } from "vue"

import {
  GetOrganizationInfoByDomainDocument,
  GetOrganizationInfoByIdDocument,
  Organization,
  OrganizationStatus,
} from "@app/helpers/backend/graphql"
import {
  getOrganizationDomain,
  getOrganizationDomainDesktop,
  isDefaultCloudInstance,
  isDesktopMode,
} from "@app/helpers/utils/cloud-for-orgs"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import { diag } from "@hoppscotch/common/kernel/log"
import { platform } from "@hoppscotch/common/platform"
import {
  BANNER_PRIORITY_HIGH,
  BANNER_PRIORITY_MEDIUM,
  BannerService,
  BannerType,
} from "@hoppscotch/common/services/banner.service"
import {
  BannerStatus as OrgBannerStatus,
  BannerType as OrgBannerType,
  OrganizationActiveBannerDocument,
} from "@app/helpers/admin/backend/enterprise-types"

// ============================================================================
// TEST MODE: Skips organization validation API calls and uses mock data instead. This lets
// you test the org-specific UI (like subscription banners, org settings, etc) without
// needing a real org backend. Set to false once the org GraphQL endpoints are available.
// ============================================================================
const SKIP_ORG_VALIDATION = false

const MOCK_ORGANIZATION: Organization = {
  orgID: "mock-org-id",
  orgName: "Test Organization",
  orgDomain: "testorg",
  orgStatus: OrganizationStatus.Active,
  orgLogo: null,
  orgSeats: 10,
  orgMembers: [],
  nextBillingDate: null,
  isFreeTrial: false,
  orgOwner: {
    uid: "mock-owner-uid",
    displayName: "Test Owner",
    email: "owner@test.com",
    photoURL: null,
  },
} as unknown as Organization
// ============================================================================

/**
 * Returns the banner type based on the number of days remaining for the license to expire
 */
const getLicenseBannerType = (daysRemaining: number): BannerType => {
  if (daysRemaining < 0) {
    return "error"
  }
  if (daysRemaining < 4) {
    return "warning"
  }
  return "info"
}

/**
 * This service compiles the necessary business logic wrt org cloud instances.
 */
export class OrganizationService extends Service {
  public static readonly ID = "ORGANIZATION_SERVICE"

  private readonly banner = this.bind(BannerService)

  private _organizationInfo = ref<Organization | null>(null)

  // TODO: Remove the below private member when the result is returned directly as an Either
  private organizationError = ref<string | null>(null)

  private _uploadingLogo = ref(false)

  // tracks the in-flight fetchOrganizationBaseInfo call so that
  // fetchOrganizationInfo can await it before checking error state.
  // without this, a race condition occurs: the route guard calls
  // fetchOrganizationInfo before fetchOrganizationBaseInfo finishes
  // waiting for auth, so the "auth/fail" error hasn't been set yet
  private _baseInfoPromise: Promise<void> | null = null

  public readonly organizationInfo = computed(
    () => this._organizationInfo.value
  )

  public readonly uploadingLogo = readonly(this._uploadingLogo)

  public setUploadingLogo(value: boolean) {
    this._uploadingLogo.value = value
  }

  override async onServiceInit() {
    diag("org-service", "onServiceInit started")
    diag("org-service", "isDesktopMode:", isDesktopMode())
    diag("org-service", "isDefaultCloudInstance:", isDefaultCloudInstance)
    diag(
      "org-service",
      "getOrganizationDomain():",
      getOrganizationDomain() ?? "(null)"
    )
    diag(
      "org-service",
      "getOrganizationDomainDesktop():",
      getOrganizationDomainDesktop() ?? "(null)"
    )
    this._baseInfoPromise = this.fetchOrganizationBaseInfo()
    await this._baseInfoPromise
    diag(
      "org-service",
      "onServiceInit complete, hasOrgInfo:",
      !!this._organizationInfo.value,
      "error:",
      this.organizationError.value ?? "(none)"
    )
  }

  private async displaySubscriptionStatusBanner() {
    await this.fetchOrganizationInfo(true)

    if (
      this.organizationError.value !== null ||
      this._organizationInfo.value === null
    ) {
      return
    }

    if (
      this._organizationInfo.value.orgStatus === OrganizationStatus.Canceled
    ) {
      this.banner.showBanner({
        type: "error",
        text: () =>
          "Your subscription has been canceled. Please contact support.",
        score: BANNER_PRIORITY_HIGH,
        dismissible: true,
      })
    } else if (this._organizationInfo.value.nextBillingDate) {
      const expiry = new Date(
        this._organizationInfo.value.nextBillingDate * 1000
      )
      const now = new Date()

      // Normalizes both dates to UTC midnight to avoid time zone differences affecting the
      // calculation. Without this, a subscription expiring at 11 PM today could show as
      // "expires in 0 days" or "expires in 1 day" depending on user's time zone.
      expiry.setHours(0, 0, 0, 0)
      now.setHours(0, 0, 0, 0)

      const diffInDays = Math.round(
        (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      )

      if (diffInDays <= 10) {
        const bannerType = getLicenseBannerType(diffInDays)

        const bannerText =
          diffInDays < 0
            ? "Your subscription has expired. To continue using Hoppscotch Orgs, please renew your subscription."
            : `Reminder: Your subscription will expire in ${diffInDays} ${
                diffInDays > 1 ? "days" : "day"
              }. Please resubscribe to continue using Hoppscotch Orgs.`

        this.banner.showBanner({
          type: bannerType,
          text: () => bannerText,
          score: BANNER_PRIORITY_MEDIUM,
          dismissible: true,
        })
      }
    }

    await this.displayActiveEnterpriseBanner()
  }

  private activeOrgBannerID: number | null = null
  private _activeEnterpriseBannerPromise: Promise<void> | null = null

  public async displayActiveEnterpriseBanner() {
    if (this._activeEnterpriseBannerPromise) {
      return this._activeEnterpriseBannerPromise
    }

    this._activeEnterpriseBannerPromise =
      this._fetchAndDisplayActiveEnterpriseBanner()
    try {
      await this._activeEnterpriseBannerPromise
    } finally {
      this._activeEnterpriseBannerPromise = null
    }
  }

  private async _fetchAndDisplayActiveEnterpriseBanner() {
    const orgID = this._organizationInfo.value?.orgID
    if (!orgID) return

    const result = await runGQLQuery({
      query: OrganizationActiveBannerDocument,
      variables: { orgID },
      skipOrgInfoWait: true,
    })

    if (E.isRight(result)) {
      const bannerData = result.right.organizationActiveBanner
      if (!bannerData || bannerData.status !== OrgBannerStatus.ACTIVE) {
        if (this.activeOrgBannerID) {
          this.banner.removeBanner(this.activeOrgBannerID)
          this.activeOrgBannerID = null
        }
        return
      }

      const bannerTypeMap: Record<OrgBannerType, BannerType> = {
        [OrgBannerType.INFO]: "info",
        [OrgBannerType.WARNING]: "warning",
        [OrgBannerType.ERROR]: "error",
      }

      const type = bannerTypeMap[bannerData.type] ?? "info"

      if (this.activeOrgBannerID) {
        this.banner.removeBanner(this.activeOrgBannerID)
      }

      this.activeOrgBannerID = this.banner.showBanner({
        type,
        text: () => bannerData.message,
        score: BANNER_PRIORITY_HIGH,
        dismissible: true,
      })
    } else {
      diag(
        "org-service",
        "fetchActiveEnterpriseBanner error:",
        result.left
      )
    }
  }

  // fetches the org ID using the org domain. on web we pull the domain from
  // the URL hostname, on desktop we pull it from the ?org= query param (all
  // desktop webviews share the same app://hoppscotch origin for IPC compat,
  // so the org host is passed as a query param instead of changing the
  // hostname). once we have the domain we wait for Firebase auth to restore
  // (desktop creates a fresh webview per org switch so auth needs time to
  // hydrate from the kernel store) and then query the backend. if there's
  // no stored session we surface "auth/fail" so the route guard sends the
  // user to /orgs/login-required instead of /orgs/invalid
  public async fetchOrganizationBaseInfo() {
    diag("org-service", "fetchOrganizationBaseInfo started")
    if (SKIP_ORG_VALIDATION) {
      this._organizationInfo.value = MOCK_ORGANIZATION
      this.organizationError.value = null
      return
    }

    if (this.organizationError.value) {
      this.organizationError.value = null
    }

    const orgDomain = isDesktopMode()
      ? getOrganizationDomainDesktop()
      : getOrganizationDomain()

    if (this._organizationInfo.value?.orgID || !orgDomain) {
      return
    }

    try {
      await platform.auth.waitProbableLoginToConfirm()
    } catch (e) {
      this.organizationError.value = "auth/fail"
      // Allow a later caller (e.g. post-login retry) to re-enter.
      this._baseInfoPromise = null
      return
    }

    const result = await runGQLQuery({
      query: GetOrganizationInfoByDomainDocument,
      variables: { orgDomain },
      // This call IS the org-info lookup. Waiting for org info here would
      // deadlock (we'd be waiting on ourselves), so opt out of the gate.
      skipOrgInfoWait: true,
    })

    if (result && E.isRight(result)) {
      const data =
        result.right.organizationByDomainV2 ??
        (result.right as any).organizationByDomain
      const orgID = typeof data === "object" ? data?.orgID : data
      const tier = typeof data === "object" ? data?.tier : undefined

      this._organizationInfo.value = <Organization>{
        ...(this._organizationInfo.value ?? {}),
        orgID,
        ...(tier ? { tier } : {}),
      }
      this.organizationError.value = null

      // clear the promise before calling banner methods.
      // displaySubscriptionStatusBanner calls fetchOrganizationInfo(true) which awaits
      // _baseInfoPromise. if we don't clear it, we deadlock: this function
      // IS _baseInfoPromise and it's waiting for the banner method to
      // finish, while the banner method awaits us.
      this._baseInfoPromise = null

      await Promise.allSettled([
        this.displaySubscriptionStatusBanner(),
        this.displayActiveEnterpriseBanner(),
      ])
    } else {
      this.organizationError.value = result.left.error.toString()
      this._baseInfoPromise = null
    }
  }

  public async fetchOrganizationInfo(refetch = false) {
    // TEST MODE: Returns mock data immediately
    if (SKIP_ORG_VALIDATION) {
      this._organizationInfo.value = MOCK_ORGANIZATION
      this.organizationError.value = null
      return
    }

    // wait for fetchOrganizationBaseInfo to finish before inspecting state.
    // onServiceInit kicks off base info fetching (which waits for auth to
    // hydrate), and the route guard can call us before that completes. without
    // this await we'd see organizationError as null, wait 3s for
    // _organizationInfo that never arrives, and set the wrong error code
    if (this._baseInfoPromise) {
      await this._baseInfoPromise
    }

    // Clear stale `auth/fail` or missing base info and refetch when a user is now present
    // (e.g. after interactive sign-in or cache clear). Honour as terminal otherwise.
    if (
      this.organizationError.value === "auth/fail" ||
      !this._organizationInfo.value?.orgID
    ) {
      if (!platform.auth.getCurrentUser()) {
        if (!this.organizationError.value) {
          this.organizationError.value = "auth/fail"
        }
        return
      }

      // Share the in-flight retry across concurrent callers. Reset
      // `_organizationInfo` so `fetchOrganizationBaseInfo` re-enters
      // the by-domain lookup instead of early-returning on a stale cache.
      if (!this._baseInfoPromise) {
        this._organizationInfo.value = null
        this.organizationError.value = null
        this._baseInfoPromise = this.fetchOrganizationBaseInfo()
      }
      await this._baseInfoPromise

      if (this.organizationError.value === "auth/fail") {
        return
      }
    }

    if (this.organizationError.value) {
      // Keep a by-domain lookup error. With the org ID known, the error is
      // from the by-ID query, so clear it and query again below, as on main.
      if (!this._organizationInfo.value?.orgID) {
        return
      }
      this.organizationError.value = null
    }

    if (!this._organizationInfo.value?.orgID) {
      // TODO: Maintain a list of error codes
      this.organizationError.value = "organization/base_info_not_available"
      return
    }

    // Prevent n/w call if the required information is already available
    const { orgID, orgDomain, orgName } = this._organizationInfo.value

    if (!refetch && orgID && orgDomain && orgName) {
      return
    }

    const result = await runGQLQuery({
      query: GetOrganizationInfoByIdDocument,
      // TODO: Supply actual domain
      variables: { orgID: this._organizationInfo.value.orgID },
    })

    // TODO: Return the either directly
    if (E.isRight(result)) {
      this._organizationInfo.value = result.right.organization as Organization
      this.organizationError.value = null
      return
    }

    const errorMessage = result.left.error.toString()

    // Retry through `fetchOrganizationBaseInfo` if the by-ID query rejects
    // with `auth/fail` while a user is present (e.g. admin re-login after
    // a non-admin sign-out left `_organizationInfo` as `{ orgID }`).
    if (errorMessage === "auth/fail" && platform.auth.getCurrentUser()) {
      this._organizationInfo.value = null
      this.organizationError.value = null
      if (!this._baseInfoPromise) {
        this._baseInfoPromise = this.fetchOrganizationBaseInfo()
      }
      await this._baseInfoPromise
      return
    }

    this.organizationError.value = errorMessage
  }

  public getOrganizationID(): string | null {
    if (isDefaultCloudInstance) {
      return null
    }

    return this._organizationInfo.value?.orgID ?? null
  }

  // Resolves once the org info lookup has settled (either the org ID is known
  // or the fetch has failed). Returns immediately when there is nothing to wait
  // for (default cloud instance, no org domain, or org info already determined).
  //
  // This is used to gate backend GQL calls so the `x-organization-id` header is
  // present before any request goes out. Without it, on a subdomain reload,
  // queries fire before `fetchOrganizationBaseInfo` completes and hit the wrong
  // org context.
  public async waitOrganizationInfoReady(): Promise<void> {
    if (isDefaultCloudInstance) {
      return
    }

    const orgDomain = isDesktopMode()
      ? getOrganizationDomainDesktop()
      : getOrganizationDomain()

    // No org domain in scope -> getBackendHeaders will never carry an org id,
    // so there is nothing to wait for.
    if (!orgDomain) {
      return
    }

    // Already resolved (success or terminal error) -> nothing to wait for.
    if (this._organizationInfo.value?.orgID) {
      return
    }
    if (this.organizationError.value) {
      return
    }

    // An in-flight fetch is running; let it settle before proceeding. It
    // resolves on both success and failure, so callers do not hang.
    if (this._baseInfoPromise) {
      await this._baseInfoPromise
    }
  }

  // Returns org info combined with error state. This is what other components use to get
  // org context. Returns null for default cloud instance since there's no org to fetch.
  public async getOrganizationInfo(): Promise<
    (Organization & { error: string | null }) | null
  > {
    if (isDefaultCloudInstance) {
      return null
    }

    await this.fetchOrganizationInfo()

    // TODO: This method might not be necessary if the result is returned directly as an Either above
    return {
      ...(this._organizationInfo.value as Organization),
      error: this.organizationError.value,
    }
  }

  public clearOrganizationInfo() {
    const { orgID } = this._organizationInfo.value ?? {}

    this._organizationInfo.value = orgID ? <Organization>{ orgID } : null
    // Reset error and in-flight marker so a subsequent login can re-fetch.
    this.organizationError.value = null
    this._baseInfoPromise = null
  }

  /**
   * Update organization logo without clearing other fields
   */
  public updateOrganizationLogo(logoUrl: string | null) {
    if (this._organizationInfo.value) {
      this._organizationInfo.value = {
        ...this._organizationInfo.value,
        orgLogo: logoUrl,
      }
    }
  }
}
